import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  TextField,
  Button
} from '@mui/material';
import { CheckCircle2, Check, X } from 'lucide-react';
import { extractName, standardInputSx } from '../utils/hubsHelpers';

const SuitabilityFaqsCard = ({
  suitabilityItems = [],
  setSuitabilityItems,
  faqs = [],
  setFaqs,
  showSnackbar
}) => {
  // Local state for adding / editing suitability items
  const [newSuitInput, setNewSuitInput] = useState('');
  const [editingSuitIdx, setEditingSuitIdx] = useState(null);
  const [editingSuitText, setEditingSuitText] = useState('');

  // Local state for adding / editing FAQs
  const [newFaqQ, setNewFaqQ] = useState('');
  const [newFaqA, setNewFaqA] = useState('');
  const [editingFaqIdx, setEditingFaqIdx] = useState(null);
  const [editingFaqQ, setEditingFaqQ] = useState('');
  const [editingFaqA, setEditingFaqA] = useState('');

  // Suitability items actions
  const handleAddSuitability = () => {
    const trimmed = newSuitInput.trim();
    if (!trimmed) return;
    setSuitabilityItems((prev) => [...prev, trimmed]);
    setNewSuitInput('');
  };

  const handleSaveEditSuit = (idx) => {
    const trimmed = editingSuitText.trim();
    if (!trimmed) return;
    setSuitabilityItems((prev) => {
      const updated = [...prev];
      updated[idx] = trimmed;
      return updated;
    });
    setEditingSuitIdx(null);
    setEditingSuitText('');
  };

  const handleDeleteSuit = (idx) => {
    setSuitabilityItems((prev) => prev.filter((_, i) => i !== idx));
  };

  // FAQ actions
  const handleAddFaq = () => {
    const q = newFaqQ.trim();
    const a = newFaqA.trim();
    if (!q || !a) {
      if (showSnackbar) showSnackbar('Both question and answer are required', 'warning');
      return;
    }
    setFaqs((prev) => [...prev, { question: q, answer: a }]);
    setNewFaqQ('');
    setNewFaqA('');
  };

  const handleSaveEditFaq = (idx) => {
    const q = editingFaqQ.trim();
    const a = editingFaqA.trim();
    if (!q || !a) return;
    setFaqs((prev) => {
      const updated = [...prev];
      updated[idx] = { question: q, answer: a };
      return updated;
    });
    setEditingFaqIdx(null);
    setEditingFaqQ('');
    setEditingFaqA('');
  };

  const handleDeleteFaq = (idx) => {
    setFaqs((prev) => prev.filter((_, i) => i !== idx));
  };

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.75 }}>
      {/* Suitability Items */}
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #E2E8F0',
          borderRadius: '8px',
          p: '14px 16px',
          boxShadow: 'none'
        }}
      >
        <Typography variant="subtitle1" sx={{ fontSize: 13.5, fontWeight: 600, color: '#1E293B', mb: 0.35, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <CheckCircle2 size={16} color="#2E7D32" /> Suitability Items
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748B', fontSize: 11, mb: 1.25 }}>
          Existing. Now with edit and delete. Renders as &quot;This is for you if&quot;.
        </Typography>

        <Box sx={{ border: '1px solid #E2E8F0', borderRadius: '6px', overflow: 'hidden', mb: 1.25 }}>
          {suitabilityItems.length > 0 ? (
            suitabilityItems.map((item, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  p: '9px 12px',
                  borderTop: idx > 0 ? '1px solid #F1F5F9' : 'none',
                  fontSize: 12.5
                }}
              >
                <Box sx={{ flex: 1 }}>
                  {editingSuitIdx === idx ? (
                    <TextField
                      size="small"
                      fullWidth
                      value={editingSuitText}
                      autoFocus
                      onChange={(e) => setEditingSuitText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveEditSuit(idx);
                        else if (e.key === 'Escape') setEditingSuitIdx(null);
                      }}
                      sx={standardInputSx}
                    />
                  ) : (
                    <span>{extractName(item)}</span>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, color: '#64748B', fontSize: 11 }}>
                  {editingSuitIdx === idx ? (
                    <>
                      <Box component="span" onClick={() => handleSaveEditSuit(idx)} sx={{ cursor: 'pointer', color: '#2E7D32' }}>
                        <Check size={14} />
                      </Box>
                      <Box component="span" onClick={() => setEditingSuitIdx(null)} sx={{ cursor: 'pointer' }}>
                        <X size={14} />
                      </Box>
                    </>
                  ) : (
                    <>
                      <Box
                        component="span"
                        onClick={() => {
                          setEditingSuitIdx(idx);
                          setEditingSuitText(extractName(item));
                        }}
                        sx={{ cursor: 'pointer', '&:hover': { color: '#803927' } }}
                      >
                        Edit
                      </Box>
                      <Box
                        component="span"
                        onClick={() => handleDeleteSuit(idx)}
                        sx={{ cursor: 'pointer', '&:hover': { color: '#DC2626' } }}
                      >
                        ✕
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            ))
          ) : (
            <Box sx={{ color: '#94A3B8', fontSize: 12, p: '10px 12px' }}>
              No suitability items added yet.
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 1 }}>
          <TextField
            size="small"
            placeholder="Add suitability item..."
            value={newSuitInput}
            onChange={(e) => setNewSuitInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddSuitability();
              }
            }}
            sx={standardInputSx}
          />
          <Button
            variant="contained"
            disableElevation disableRipple
            onClick={handleAddSuitability}
            sx={{
              backgroundColor: '#1E293B',
              color: '#FFFFFF',
              borderRadius: '6px',
              px: 2,
              fontSize: 12.5,
              fontWeight: 500,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#334155', boxShadow: 'none' }
            }}
          >
            + Add
          </Button>
        </Box>
      </Paper>

      {/* Frequently Asked Questions */}
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #E2E8F0',
          borderRadius: '8px',
          p: '14px 16px',
          boxShadow: 'none'
        }}
      >
        <Typography variant="subtitle1" sx={{ fontSize: 13.5, fontWeight: 600, color: '#1E293B', mb: 0.35, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <CheckCircle2 size={16} color="#2E7D32" /> Frequently Asked Questions
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748B', fontSize: 11, mb: 1.25 }}>
          Existing. Now with edit and delete. Feeds the FAQ block and FAQ schema.
        </Typography>

        <Box sx={{ border: '1px solid #E2E8F0', borderRadius: '6px', overflow: 'hidden', mb: 1.25 }}>
          {faqs.length > 0 ? (
            faqs.map((f, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  p: '9px 12px',
                  borderTop: idx > 0 ? '1px solid #F1F5F9' : 'none',
                  fontSize: 12.5
                }}
              >
                <Box sx={{ flex: 1 }}>
                  {editingFaqIdx === idx ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      <TextField
                        size="small"
                        fullWidth
                        value={editingFaqQ}
                        autoFocus
                        onChange={(e) => setEditingFaqQ(e.target.value)}
                        sx={standardInputSx}
                      />
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        rows={2}
                        value={editingFaqA}
                        onChange={(e) => setEditingFaqA(e.target.value)}
                        sx={{
                          ...standardInputSx,
                          '& .MuiOutlinedInput-root': {
                            ...standardInputSx['& .MuiOutlinedInput-root'],
                            height: 'auto',
                            py: '8px'
                          }
                        }}
                      />
                    </Box>
                  ) : (
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: 12.5, color: '#1E293B' }}>{extractName(f.question)}</Typography>
                      <Typography sx={{ color: '#64748B', fontSize: 11.5, mt: 0.25 }}>{extractName(f.answer)}</Typography>
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, color: '#64748B', fontSize: 11 }}>
                  {editingFaqIdx === idx ? (
                    <>
                      <Box component="span" onClick={() => handleSaveEditFaq(idx)} sx={{ cursor: 'pointer', color: '#2E7D32' }}>
                        <Check size={14} />
                      </Box>
                      <Box component="span" onClick={() => setEditingFaqIdx(null)} sx={{ cursor: 'pointer' }}>
                        <X size={14} />
                      </Box>
                    </>
                  ) : (
                    <>
                      <Box
                        component="span"
                        onClick={() => {
                          setEditingFaqIdx(idx);
                          setEditingFaqQ(extractName(f.question));
                          setEditingFaqA(extractName(f.answer));
                        }}
                        sx={{ cursor: 'pointer', '&:hover': { color: '#803927' } }}
                      >
                        Edit
                      </Box>
                      <Box
                        component="span"
                        onClick={() => handleDeleteFaq(idx)}
                        sx={{ cursor: 'pointer', '&:hover': { color: '#DC2626' } }}
                      >
                        ✕
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            ))
          ) : (
            <Box sx={{ color: '#94A3B8', fontSize: 12, p: '10px 12px' }}>
              No FAQs added yet.
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Question"
            value={newFaqQ}
            onChange={(e) => setNewFaqQ(e.target.value)}
            sx={standardInputSx}
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 1 }}>
            <TextField
              size="small"
              placeholder="Answer"
              value={newFaqA}
              onChange={(e) => setNewFaqA(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddFaq();
                }
              }}
              sx={standardInputSx}
            />
            <Button
              variant="contained"
              disableElevation disableRipple
              onClick={handleAddFaq}
              sx={{
                backgroundColor: '#1E293B',
                color: '#FFFFFF',
                borderRadius: '6px',
                px: 2,
                fontSize: 12.5,
                fontWeight: 500,
                textTransform: 'none',
                boxShadow: 'none',
                whiteSpace: 'nowrap',
                '&:hover': { backgroundColor: '#334155', boxShadow: 'none' }
              }}
            >
              + Add FAQ
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default React.memo(SuitabilityFaqsCard);
